import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/Card";
import QuestionsUI from "./component/questions/QuestionsUI";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import useInterviewData from "./hooks/useInterviewData";
import useInterviewGeneration from "./hooks/useInterviewGeneration";
import { CandidateService } from "@/services/candidateService";
import toast from "react-hot-toast";
import useLiveResource from "@/hooks/useLiveResource";
import { FALLBACK_TYPES, formatTypeLabel } from "./constants/interviewTypes";
import { MessageSquareOff } from "lucide-react";

// Infers question types from existing data keys when job config is unavailable
const inferTypesFromData = (data) =>
  Object.keys(data || {})
    .filter((k) => k !== "id")
    .map((k) => ({ value: k, label: formatTypeLabel(k) }));

const Interview = ({ data, currentStatus, candidateId, applicationId, jobId, onRefresh }) => {
  // `useVisibleTabs` decides whether this tab exists at all. Two states can
  // put us here, and they differ only in what is offered:
  //
  // - Entitled → normal, minus generation while the module is dark.
  // - Not entitled, questions already stored → they stay readable and
  //   downloadable, and nothing offers to generate more.
  const {
    isEnabled: moduleEnabled,
    isAvailable: moduleAvailable,
  } = useModuleAccess(MODULES.AI_INTERVIEW_QUESTIONS);
  const [configuredTypes, setConfiguredTypes] = useState([]);
  // Distinguishes "haven't fetched configured types yet" from "fetched and
  // there genuinely are none" — both start as an empty configuredTypes array,
  // but only the latter should trigger the "No question types configured"
  // empty state below.
  const [configuredTypesLoaded, setConfiguredTypesLoaded] = useState(false);
  const [seniorityLevels, setSeniorityLevels] = useState([]);

  useEffect(() => {
    // Seniority is a generation input, and `/ai-tools/enums/seniority-levels`
    // is entitlement-gated. Without the module there is nothing to generate
    // and nothing to ask for — asking anyway is one silent 403 per candidate.
    if (!moduleEnabled) return;
    CandidateService.fetchSeniorityLevels()
      .then((levels) => setSeniorityLevels(Array.isArray(levels) ? levels : []))
      .catch(() => {});
  }, [moduleEnabled]);

  useEffect(() => {
    // Without the module there is nothing to configure and nothing to
    // generate, and `/ai-tools/interview-question-types/active` is gated — so
    // the types come from the stored questions themselves. That also gives the
    // read-only view exactly the sections it has data for.
    if (!jobId || !moduleEnabled) {
      const inferred = inferTypesFromData(data);
      setConfiguredTypes(moduleEnabled && inferred.length === 0 ? FALLBACK_TYPES : inferred);
      setConfiguredTypesLoaded(true);
      return;
    }
    CandidateService.fetchJobInterviewQuestionTypes(jobId)
      .then((types) => {
        setConfiguredTypes(Array.isArray(types) ? types : FALLBACK_TYPES);
      })
      .catch(() => {
        setConfiguredTypes(FALLBACK_TYPES);
      })
      .finally(() => {
        setConfiguredTypesLoaded(true);
      });
  }, [jobId, data?.id, moduleEnabled]);

  const {
    state: genState,
    anyGenerating,
    needsRefetch,
    handleRegenerate,
    clearRefetch,
  } = useInterviewGeneration(candidateId, applicationId, configuredTypes);

  const genStateRef = useRef(genState);
  useEffect(() => {
    genStateRef.current = genState;
  }, [genState]);

  // A job's configured interview types can change mid-flight. `job.updated`
  // carries the job's id and nothing else, so the new list comes from the
  // route — and the deactivation notice comes from diffing the list on screen
  // against the one that arrives, which is what this already did.
  const applyConfiguredTypes = useCallback((types) => {
    setConfiguredTypes((prev) => {
      const next = Array.isArray(types) && types.length ? types : FALLBACK_TYPES;
      const nextKeys = new Set(next.map((t) => t.value));
      const deactivatedInProgress = prev.filter((prevType) => !nextKeys.has(prevType.value) && genStateRef.current?.[prevType.value]?.regenProgress);

      if (deactivatedInProgress.length === 1) {
        toast(
          `${deactivatedInProgress[0].label} question type was deactivated for this job. Your in-progress generation will finish and the questions will be retained.`,
        );
      } else if (deactivatedInProgress.length > 1) {
        const labels = deactivatedInProgress.map((t) => t.label).join(", ");
        toast(`${labels} question types were deactivated for this job. In-progress generations will finish and questions will be retained.`);
      }
      return next;
    });
  }, []);

  useLiveResource({
    keys: `job:${jobId}`,
    enabled: Boolean(jobId && moduleEnabled),
    onChange: () => {
      CandidateService.fetchJobInterviewQuestionTypes(jobId)
        .then(applyConfiguredTypes)
        .catch(() => {});
    },
  });

  const { types: questionTypes, allTypeEntries } = useInterviewData(data, genState, configuredTypes);

  const hasGeneratedQuestions = Object.entries(data || {}).some(
    ([key, value]) =>
      key !== "id" &&
      value != null &&
      typeof value === "object" &&
      ((Array.isArray(value) && value.length > 0) ||
        (Array.isArray(value?.questions_data?.questions) && value.questions_data.questions.length > 0) ||
        (Array.isArray(value?.questions) && value.questions.length > 0))
  );

  const hasLiveData = Object.values(genState || {}).some(
    (v) => v?.liveData?.questions?.length > 0
  );

  // Generation is gated by entitlement and by availability. Either going
  // false leaves previously generated questions rendering and simply does
  // not offer Generate / Regenerate. Only the availability case gets a
  // banner: it is temporary and the reader needs to know it comes back. Not
  // entitled gets no banner, because a sentence about a feature the tenant
  // never bought is an advert.
  const canGenerateQuestions = moduleEnabled && moduleAvailable;

  useEffect(() => {
    if (!needsRefetch) return;
    clearRefetch();
    onRefresh?.();
  }, [needsRefetch, clearRefetch, onRefresh]);

  // There is something to show as soon as one question type is in play —
  // configured on the job, or present in stored data, or mid-generation.
  //
  // This used to read `allTypeEntries.length > 0 || … || configuredTypes.length
  // === 0 || …`, which is true for every possible input: `allTypeEntries`
  // always contains every configured type, so one of the first and fourth
  // terms always held. The empty branch below could never render, and a job
  // that had selected no question types showed a blank panel instead — the
  // reader could not tell an empty configuration from a fault.
  const hasQuestionContent =
    allTypeEntries.length > 0 ||
    hasGeneratedQuestions ||
    hasLiveData ||
    !configuredTypesLoaded ||
    anyGenerating ||
    needsRefetch;

  return (
    <div className="mb-4 h-[calc(100vh-65px)] flex flex-col">
      {/* `mx-4 mt-4`: this container has no padding of its own — the Card below
          is `p-0` and its children carry their own — so an unpadded banner sat
          flush against both edges of the panel.

          The notice renders only in the unavailable state, which requires the
          tenant to be entitled. That matters here: `isAvailable` is false for a
          tenant who never bought the module either, and telling them a tool is
          "temporarily unavailable" would leave them waiting for something that
          is never coming back. */}
      <ModuleUnavailableNotice module={MODULES.AI_INTERVIEW_QUESTIONS} suffix="Questions already generated are shown below." className="mx-4 mt-4 mb-2" />
      <Card className="border-none p-0 flex-1 min-h-0 flex flex-col">
        {!hasQuestionContent ? (
          // ── Empty state: the job selected no question types ───────────────
          //
          // The third of three empty states this tab can be in, and the one
          // that had no wording of its own. The other two are elsewhere on
          // purpose: the module being dark fleet-wide is the banner above, and
          // a candidate with no questions yet is handled per type inside
          // QuestionsUI, next to the button that fixes it. Keeping them apart
          // is the whole point — each names a different reason and a different
          // person can act on it.
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background-tertiary text-text-muted">
              <MessageSquareOff className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-text-primary">This job has no interview question types</p>
              <p className="max-w-[300px] text-xs text-text-muted">
                Questions are generated per type, and none is selected for this job. Choose the types you want in the job's configuration, then come back.
              </p>
            </div>
          </div>
        ) : (
           <QuestionsUI
            questionTypes={questionTypes}
            configuredTypes={configuredTypes}
            allTypeEntries={allTypeEntries}
            onRegenerate={handleRegenerate}
            seniorityLevels={seniorityLevels}
            canGenerateQuestions={canGenerateQuestions}
          />
        )}
      </Card>
    </div>
  );
};

export default Interview;
